/**
 * AdminImportarDadosTab — Importação de extratos de corretoras
 * Sprint 6: Extraído de AdminPanel.jsx
 */
import PropTypes from "prop-types";
import { lazy, Suspense, useState } from "react";
import FileUploadSection from "./importar/FileUploadSection.jsx";
import PreviewSection from "./importar/PreviewSection.jsx";
import AssetTablesSection from "./importar/AssetTablesSection.jsx";
import ImportFormSection from "./importar/ImportFormSection.jsx";
import SuccessSection from "./importar/SuccessSection.jsx";
import UploadHistorySection from "./importar/UploadHistorySection.jsx";
import { Card } from "./adminUtils.jsx";
import { useClientContext } from "../ClientContext.jsx";
import { LpaButton } from "../components/ui/lpa";

// Inter Win parser (lazy — só carrega quando AAI clica)
const InterWinImportPanel = lazy(() => import("./components/InterWinImportPanel.tsx"));

export default function AdminImportarDadosTab({
  dynamicClients,
  uploads,
  importState,
  importBroker,
  importParsed,
  importWarnings,
  importClientId,
  importPeriod,
  importRefDate,
  importFileObj,
  drag,
  fileRef,
  setDrag,
  setImportBroker,
  setImportClientId,
  setImportPeriod,
  setImportRefDate,
  handleImportFile,
  handleImportConfirm,
  handleImportReset,
  onRunAnalysis,
  analysisLoading,
}) {
  const { T } = useClientContext();
  const [mode, setMode] = useState("legacy"); // "legacy" | "interwin"

  return (
    <div>
      {/* ── Mode switcher: Inter Win PDF vs corretora legacy ── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: "1px solid var(--lpa-border)",
        }}
      >
        <LpaButton
          variant={mode === "interwin" ? "primary" : "ghost"}
          size="sm"
          onClick={() => setMode("interwin")}
        >
          📄 Inter Win — Relatório Consolidado (PDF)
        </LpaButton>
        <LpaButton
          variant={mode === "legacy" ? "primary" : "ghost"}
          size="sm"
          onClick={() => setMode("legacy")}
        >
          📊 Extrato XP/BTG (legacy parser)
        </LpaButton>
      </div>

      {mode === "interwin" && (
        <Suspense
          fallback={
            <div style={{ padding: 24, color: "var(--lpa-muted)" }}>
              Carregando parser Inter Win…
            </div>
          }
        >
          <InterWinImportPanel dynamicClients={dynamicClients} />
        </Suspense>
      )}

      {mode === "legacy" && (
        <>
          {/* ── STEP 1: Drop zones ── */}
          <FileUploadSection
            importState={importState}
            drag={drag}
            fileRef={fileRef}
            setDrag={setDrag}
            setImportBroker={setImportBroker}
            importBroker={importBroker}
            handleImportFile={handleImportFile}
          />

          {/* ── STEP 2: Preview + Asset Tables + formulário de confirmação ── */}
          {(importState === "preview" || importState === "saving" || importState === "done") &&
            importParsed && (
              <>
                <PreviewSection
                  importState={importState}
                  importBroker={importBroker}
                  importFileObj={importFileObj}
                  importParsed={importParsed}
                  importWarnings={importWarnings}
                  handleImportReset={handleImportReset}
                />

                {/* Asset Tables (only for non-IR imports) */}
                <Card T={T} style={{ marginBottom: 20 }}>
                  <AssetTablesSection importParsed={importParsed} />

                  {/* Import Form */}
                  <ImportFormSection
                    importState={importState}
                    importParsed={importParsed}
                    importPeriod={importPeriod}
                    importRefDate={importRefDate}
                    importClientId={importClientId}
                    dynamicClients={dynamicClients}
                    setImportPeriod={setImportPeriod}
                    setImportRefDate={setImportRefDate}
                    setImportClientId={setImportClientId}
                    handleImportConfirm={handleImportConfirm}
                  />

                  {/* Success Message */}
                  <SuccessSection
                    importState={importState}
                    importPeriod={importPeriod}
                    handleImportReset={handleImportReset}
                    onRunAnalysis={onRunAnalysis}
                    analysisLoading={analysisLoading}
                  />
                </Card>
              </>
            )}

          {/* ── Histórico de importações ── */}
          <UploadHistorySection uploads={uploads} />
        </>
      )}
    </div>
  );
}

AdminImportarDadosTab.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  uploads: PropTypes.array.isRequired,
  importState: PropTypes.string.isRequired,
  importBroker: PropTypes.string.isRequired,
  importParsed: PropTypes.object,
  importWarnings: PropTypes.array.isRequired,
  importClientId: PropTypes.string.isRequired,
  importPeriod: PropTypes.string.isRequired,
  importRefDate: PropTypes.string.isRequired,
  importFileObj: PropTypes.object,
  drag: PropTypes.object.isRequired,
  fileRef: PropTypes.object.isRequired,
  setDrag: PropTypes.func.isRequired,
  setImportBroker: PropTypes.func.isRequired,
  setImportClientId: PropTypes.func.isRequired,
  setImportPeriod: PropTypes.func.isRequired,
  setImportRefDate: PropTypes.func.isRequired,
  handleImportFile: PropTypes.func.isRequired,
  handleImportConfirm: PropTypes.func.isRequired,
  handleImportReset: PropTypes.func.isRequired,
  onRunAnalysis: PropTypes.func,
  analysisLoading: PropTypes.bool,
};
